import type { Attachment, AttachmentAdapter, CompleteAttachment, PendingAttachment } from "@assistant-ui/react";
import { message } from "antd";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const REJECTED_PREFIX = "rejected_";

export interface GcsAttachmentAdapterOptions {
  baseUrl: string;
  getHeaders: () => Record<string, string>;
  getThreadId: () => string | null;
}

interface UploadResult {
  attachmentId: string;
  filename: string;
}

/**
 * Custom AttachmentAdapter that uploads files to GCS via the Superdoc
 * attachment API (signed URLs) and passes attachment IDs to the LLM.
 *
 * Uploads start eagerly when a file is selected (in `add()`) so the
 * upload runs while the user is still typing their message. By the
 * time they hit send, the file is usually already on GCS.
 */
export class GcsAttachmentAdapter implements AttachmentAdapter {
  accept = ".pdf,.txt,.jpg,.jpeg,.png";

  private baseUrl: string;
  private getHeaders: () => Record<string, string>;
  private getThreadId: () => string | null;

  /** Tracks in-flight uploads keyed by pending attachment ID. */
  private uploads = new Map<string, Promise<UploadResult>>();

  constructor(options: GcsAttachmentAdapterOptions) {
    this.baseUrl = options.baseUrl;
    this.getHeaders = options.getHeaders;
    this.getThreadId = options.getThreadId;
  }

  async add({ file }: { file: File }): Promise<PendingAttachment> {
    if (file.size > MAX_FILE_SIZE) {
      message.error(`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
      // Return a rejected placeholder — send() will skip it
      return {
        id: `${REJECTED_PREFIX}${Date.now()}`,
        type: "document",
        name: file.name,
        contentType: file.type,
        file,
        status: { type: "requires-action", reason: "composer-send" },
      };
    }

    const type = file.type.startsWith("image/") ? "image" : "document";
    const pendingId = `pending_${Date.now()}_${file.name}`;

    // Start uploading eagerly — runs in the background while user types
    this.uploads.set(pendingId, this._upload(file));

    return {
      id: pendingId,
      type,
      name: file.name,
      contentType: file.type,
      file,
      status: { type: "requires-action", reason: "composer-send" },
    };
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    // Skip rejected attachments (e.g. file too large)
    if (attachment.id.startsWith(REJECTED_PREFIX)) {
      return {
        ...attachment,
        status: { type: "complete" },
        content: [],
      };
    }

    // Await the eager upload that started in add()
    const uploadPromise = this.uploads.get(attachment.id);
    const result = uploadPromise ? await uploadPromise : await this._upload(attachment.file);
    this.uploads.delete(attachment.id);

    return {
      ...attachment,
      id: result.attachmentId,
      status: { type: "complete" },
      content: [
        {
          type: "text",
          text: `[Attached file: ${result.filename}]`,
        },
      ],
    };
  }

  async remove(attachment: Attachment): Promise<void> {
    // Cancel/clean up eager upload if still pending
    this.uploads.delete(attachment.id);

    // Only delete from backend if it has a real (non-pending) ID
    if (attachment.id && !attachment.id.startsWith("pending_")) {
      try {
        await fetch(`${this.baseUrl}/superdoc/attachments/${attachment.id}/`, {
          method: "DELETE",
          headers: this.getHeaders(),
        });
      } catch {
        // Best-effort cleanup
      }
    }
  }

  /** Create attachment record + upload file to GCS. */
  private async _upload(file: File): Promise<UploadResult> {
    const threadId = this.getThreadId();

    // Step 1: Create attachment record and get signed upload URL
    const createRes = await fetch(`${this.baseUrl}/superdoc/attachments/create/`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        thread_id: threadId,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        size: file.size,
      }),
    });

    if (!createRes.ok) {
      const err = await createRes.text();
      throw new Error(`Failed to create attachment: ${err}`);
    }

    const { id: attachmentId, upload_url } = (await createRes.json()) as {
      id: string;
      upload_url: string;
    };

    // Step 2: Upload file directly to GCS via signed PUT URL
    const uploadRes = await fetch(upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error(`Failed to upload file to GCS: ${uploadRes.status}`);
    }

    return { attachmentId, filename: file.name };
  }
}
